'use client';

import type { PostSearchProps } from '@routeroyal/theme-core';
import { useEffect, useRef, useState } from 'react';

/**
 * AllysPostSearch - Allys-themed post search
 * Search input with Allys styling and debouncing
 */
export function AllysPostSearch({
  value = '',
  onChange,
  placeholder = 'Search posts...',
  className = '',
}: PostSearchProps) {
  const [localValue, setLocalValue] = useState(value);
  const debounceRef = useRef<NodeJS.Timeout>(undefined);
  const lastEmittedRef = useRef(value);

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      setLocalValue(value);
      lastEmittedRef.current = value;
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleInputChange = (newValue: string) => {
    setLocalValue(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastEmittedRef.current = newValue;
      onChange(newValue);
    }, 300);
  };

  const handleClear = () => {
    setLocalValue('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastEmittedRef.current = '';
    onChange('');
  };

  return (
    <div className={`relative [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}>
      {/* Search Icon */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#BBA339] pointer-events-none">
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Input */}
      <input
        type="text"
        value={localValue}
        onChange={(e) => handleInputChange(e.target.value)}
        placeholder={placeholder}
        className="
          w-full pl-12 pr-12 py-3 rounded-xl
          border-2 border-gray-200 bg-white
          text-[#243462] placeholder-gray-400
          focus:ring-0 focus:border-[#BBA339]
          transition-all duration-300
          hover:border-[#BBA339]/50
        "
      />

      {/* Clear Button */}
      {localValue && (
        <button
          type="button"
          onClick={handleClear}
          className="
            absolute right-4 top-1/2 -translate-y-1/2
            text-gray-400 hover:text-[#243462]
            transition-colors duration-300
          "
          aria-label="Clear search"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
